import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Search, ShoppingBag, Heart, Menu, Phone, ChevronDown, X } from 'lucide-react';
import { SITE, CATEGORIES } from '../data/mock';
import { useCart } from '../context/CartContext';
import { Input } from './ui/input';

const MobileNavSheet = lazy(() => import('./MobileNavSheet'));

const NAV = [
  { to: '/catalog', label: 'Каталог' },
  { to: '/partners', label: 'Партнерські продукти' },
  { to: '/vitamin-test', label: 'Тест на дефіцит вітамінів' },
  { to: '/blog', label: 'Блог' },
  { to: '/about', label: 'Про нас' },
  { to: '/payment-delivery', label: 'Оплата та доставка' },
  { to: '/contacts', label: 'Контакти' },
];

const Header = () => {
  const { count, wishlist } = useCart();
  const [mobOpen, setMobOpen] = useState(false);
  const [mobSearchOpen, setMobSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Sync search input with URL q param
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setSearch(p.get('q') || '');
  }, [location.search]);

  const submitSearch = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!search.trim()) return navigate('/catalog');
    navigate(`/catalog?q=${encodeURIComponent(search.trim())}`);
    setMobOpen(false);
    setMobSearchOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-white border-b border-[var(--msa-line)]">
      {/* Top utility row — desktop only */}
      <div className="hidden lg:block border-b border-[var(--msa-line)]">
        <div className="max-w-[1320px] mx-auto px-6 py-1.5 flex items-center justify-between text-[12.5px] text-[var(--msa-ink-soft)]">
          <div className="flex items-center gap-5">
            <span>Графік: {SITE.hours}</span>
          </div>
          <div className="flex items-center gap-5">
            <a href={SITE.instagram} target="_blank" rel="noreferrer" className="hover:text-[var(--msa-green)] transition-colors">Instagram {SITE.instagramHandle}</a>
            <a href={`tel:${SITE.phoneRaw}`} className="inline-flex items-center gap-1.5 hover:text-[var(--msa-green)] transition-colors"><Phone className="w-3.5 h-3.5" />{SITE.phone}</a>
          </div>
        </div>
      </div>

      {/* Main row */}
      <div className="relative max-w-[1320px] mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-2 sm:gap-5">
        <div className="flex items-center gap-2.5 sm:gap-4 shrink-0">
          <button onClick={() => setMobOpen(true)} className="lg:hidden p-2 -ml-1 rounded-lg hover:bg-[var(--msa-bg-soft)]" aria-label="Меню">
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/" className="shrink-0 flex items-center">
            <img src="/logo.svg?v=20260505-1148" alt="MSA" width="136" height="42" className="h-[42px] sm:h-[44px] lg:h-[52px] w-auto object-contain origin-center" />
          </Link>
        </div>

        {/* Search desktop */}
        <form onSubmit={submitSearch} className="hidden lg:flex flex-1 mx-4 max-w-[640px]">
          <div className="relative w-full">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--msa-ink-soft)]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Шукати добавки, вітаміни, мінерали…"
              className="pl-10 pr-4 h-11 rounded-full border-[var(--msa-line)] focus-visible:ring-[var(--msa-green)]/40"
            />
          </div>
        </form>

        <nav className="hidden xl:flex items-center gap-6 text-sm font-semibold text-[var(--msa-ink)]">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `relative py-1 transition-colors hover:text-[var(--msa-green)] ${isActive ? 'text-[var(--msa-green)]' : ''}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
          <button onClick={() => setMobSearchOpen((v) => !v)} className="lg:hidden p-2 rounded-full hover:bg-[var(--msa-bg-soft)]" aria-label="Пошук" aria-expanded={mobSearchOpen}>
            {mobSearchOpen ? <X className="w-[18px] h-[18px]" /> : <Search className="w-[18px] h-[18px]" />}
          </button>
          <Link to="/wishlist" className="relative flex p-2 rounded-full hover:bg-[var(--msa-bg-soft)] transition-colors" aria-label="Список бажань">
            <Heart className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
            {wishlist.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 text-[10px] font-bold rounded-full bg-[var(--msa-green-cta)] text-white flex items-center justify-center">{wishlist.length}</span>
            )}
          </Link>
          <Link to="/cart" className="relative flex p-2 rounded-full hover:bg-[var(--msa-bg-soft)] transition-colors" aria-label="Кошик">
            <ShoppingBag className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-[var(--msa-green-cta)] text-white flex items-center justify-center">{count}</span>
            )}
          </Link>
        </div>
      </div>

      {/* Inline mobile search — toggled by the magnifier icon */}
      {mobSearchOpen && (
        <div className="lg:hidden border-t border-[var(--msa-line)] px-3 py-2.5">
          <form onSubmit={submitSearch} className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--msa-ink-soft)]" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Шукати добавки, вітаміни, мінерали…"
              className="pl-10 pr-4 h-11 rounded-full border-[var(--msa-line)] focus-visible:ring-[var(--msa-green)]/40"
            />
          </form>
        </div>
      )}

      {/* Categories nav strip — desktop */}
      <div className="hidden xl:block border-t border-[var(--msa-line)] bg-white">
        <div className="max-w-[1320px] mx-auto px-6">
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar py-2.5">
            <Link to="/catalog" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white bg-[var(--msa-ink)] hover:bg-[var(--msa-green-dark)] px-3.5 py-2 rounded-full transition-colors mr-2">
              <Menu className="w-3.5 h-3.5" /> Всі категорії <ChevronDown className="w-3.5 h-3.5" />
            </Link>
            {CATEGORIES.slice(1, 9).map((c) => (
              <Link key={c.slug} to={`/catalog/${c.slug}`} className="text-[13px] text-[var(--msa-ink-soft)] hover:text-[var(--msa-green)] hover:bg-[var(--msa-bg-soft)] px-3 py-2 rounded-full transition-colors whitespace-nowrap">
                {c.name}
              </Link>
            ))}
            <Link to="/partners" className="text-[13px] text-[var(--msa-green-dark)] hover:text-white hover:bg-[var(--msa-green-dark)] border border-[var(--msa-green-dark)] px-3 py-2 rounded-full transition-colors whitespace-nowrap font-semibold ml-1">
              🤝 Партнерські товари
            </Link>
          </div>
        </div>
      </div>

      {mobOpen && (
        <Suspense fallback={null}>
          <MobileNavSheet open={mobOpen} onOpenChange={setMobOpen} search={search} setSearch={setSearch} submitSearch={submitSearch} />
        </Suspense>
      )}
    </header>
  );
};

export default Header;
